// Builds the run_end payload from a *previous* store state — never from
// useGameStore.getState() at the point the transition is observed. See the
// warning in bind.ts: freshRunState()/resetGame() have already wiped
// score/runTargetEvents by the time a Zustand subscriber callback runs.
import { getPerformanceStats } from "../game/runStats";
import type {
  GameMode,
  GradeCounts,
  TargetRunEvent,
} from "../game/useGameStore";
import type {
  CalibrationHistogram,
  CalibrationSummary,
  RunEndProps,
  RunOutcome,
} from "./events";

const HISTOGRAM_BUCKETS = 8;

function percentile(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null;
  const index = Math.min(
    sorted.length - 1,
    Math.max(0, Math.round(p * (sorted.length - 1))),
  );
  return sorted[index];
}

function buildHistogram(values: number[]): CalibrationHistogram | null {
  if (values.length === 0) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const counts = new Array(HISTOGRAM_BUCKETS).fill(0);
  const span = max - min;
  for (const value of values) {
    const bucket =
      span === 0
        ? 0
        : Math.min(
            HISTOGRAM_BUCKETS - 1,
            Math.floor(((value - min) / span) * HISTOGRAM_BUCKETS),
          );
    counts[bucket] += 1;
  }
  return { min, max, counts };
}

function buildCalibrationSummary(
  targetEvents: TargetRunEvent[],
): CalibrationSummary {
  const timingSigned = targetEvents
    .map((event) => event.timingSigned)
    .filter((value): value is number => value != null)
    .sort((a, b) => a - b);
  const snapValues = targetEvents
    .map((event) => event.snap)
    .filter((value): value is number => value != null);
  const bladeSpeedValues = targetEvents
    .map((event) => event.bladeSpeed)
    .filter((value): value is number => value != null);

  return {
    timing_signed_p10: percentile(timingSigned, 0.1),
    timing_signed_median: percentile(timingSigned, 0.5),
    timing_signed_p90: percentile(timingSigned, 0.9),
    snap_hist: buildHistogram(snapValues),
    blade_speed_hist: buildHistogram(bladeSpeedValues),
  };
}

export interface RunSnapshot {
  mode: GameMode;
  score: number;
  peakCombo: number;
  gradeCounts: GradeCounts;
  runKataLanded: number;
  runKataTotal: number;
  runKataBonus: number;
  runTargetEvents: TargetRunEvent[];
  runObstacleEvents: import("../game/useGameStore").ObstacleRunEvent[];
  lastRunPassed: boolean | null;
  lastRunPhaseIndex: number;
  lastRunThreshold: number;
  lastSurvivalWaves: number;
  lastSurvivalTrackIndex: number;
}

export function buildRunEndProps(
  snapshot: RunSnapshot,
  outcome: RunOutcome,
  durationS: number,
): RunEndProps {
  const stats = getPerformanceStats(
    snapshot.runTargetEvents,
    snapshot.runObstacleEvents,
  );
  const isCampaign = snapshot.mode === "campaign";

  return {
    mode: snapshot.mode,
    phase_index: isCampaign ? snapshot.lastRunPhaseIndex : null,
    outcome,
    score: snapshot.score,
    threshold: isCampaign ? snapshot.lastRunThreshold : null,
    passed: isCampaign ? snapshot.lastRunPassed : null,
    peak_combo: snapshot.peakCombo,
    duration_s: Math.round(durationS * 10) / 10,
    hit_rate: stats.hitRate,
    targets_total: stats.totalTargets,
    targets_hit: stats.hitTargets,
    targets_missed: stats.missedTargets,
    targets_wrong_dir: stats.wrongDirectionTargets,
    grades: snapshot.gradeCounts,
    kata_landed: snapshot.runKataLanded,
    kata_total: snapshot.runKataTotal,
    kata_bonus: snapshot.runKataBonus,
    tenouchi_hits: stats.tenouchiHits,
    obstacles_total: stats.obstacleTotal,
    obstacles_hit: stats.obstacleHit,
    waves: isCampaign ? null : snapshot.lastSurvivalWaves,
    track_index: isCampaign ? null : snapshot.lastSurvivalTrackIndex,
    ...buildCalibrationSummary(snapshot.runTargetEvents),
  };
}
